import { ReactNode } from 'react';

/** A section of a page or tab: no box, one thin rule above it, whitespace between sections. */
export function Panel({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <section className={`sec pt-6 border-t border-line-strong max-md:pt-4 ${className}`}>{children}</section>;
}

/** Section head: serif title, its note after it, actions on the right (under the title on a phone). */
export function PanelHeader({
  title,
  note,
  children,
}: {
  title: ReactNode;
  note?: ReactNode;
  children?: ReactNode;
  /** Accepted for older callers; sections have no rule under the head. */
  rule?: boolean;
}) {
  return (
    <header className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-2 mb-3">
      <h2 className="font-serif font-semibold text-heading text-ink">
        {title}
        {note ? <span className="font-sans font-normal text-[15px] text-ink-3"> · {note}</span> : null}
      </h2>
      {children ? <div className="flex flex-wrap items-center gap-x-4 gap-y-2 max-md:w-full">{children}</div> : null}
    </header>
  );
}

export function PanelBody({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <div className={className}>{children}</div>;
}

/** A page's or tab's opening: the serif sentence that answers the main question, then one line on what it is for. */
export function Lead({ lead, sub, children }: { lead?: ReactNode; sub?: ReactNode; children?: ReactNode }) {
  if (!lead && !sub && !children) return null;
  return (
    <div className="flex flex-wrap items-end justify-between gap-x-12 gap-y-6 mb-2">
      <div className="flex-[1_1_560px] min-w-0">
        {lead ? (
          <p className="font-serif text-lead text-ink max-w-[820px] max-md:text-[20px] max-md:leading-[29px]">{lead}</p>
        ) : null}
        {sub ? <p className="text-[15px] leading-[22px] text-ink-2 mt-2 max-w-[72ch]">{sub}</p> : null}
      </div>
      {children ? <div className="flex flex-wrap gap-3 max-md:w-full max-md:[&>button]:w-full">{children}</div> : null}
    </div>
  );
}

export function PageHeader({
  title,
  lead,
  sub,
  eyebrow,
  aside,
}: {
  title: string;
  lead?: ReactNode;
  sub?: ReactNode;
  eyebrow?: ReactNode;
  aside?: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-x-12 gap-y-4 mb-8">
      <div className="min-w-0 flex-[1_1_560px]">
        {eyebrow ? <div className="mb-2">{eyebrow}</div> : null}
        <h1 className="font-serif font-semibold text-display text-ink max-md:text-[26px] max-md:leading-8">{title}</h1>
        {lead ? (
          <p className="font-serif text-lead text-ink mt-3 max-w-[820px] max-md:text-[20px] max-md:leading-[29px]">
            {lead}
          </p>
        ) : null}
        {sub ? <p className="text-[15px] leading-[22px] text-ink-2 mt-2 max-w-[72ch]">{sub}</p> : null}
      </div>
      {aside ? <div className="flex flex-wrap items-center gap-3 max-md:w-full">{aside}</div> : null}
    </div>
  );
}
